import React from 'react';
import { safeJsonParse } from '../../../../utils/safeJsonParse';
import styles from './index.module.scss';

const ReviewsV1 = ({ publication, globalIndex }) => {
  const [publicationData, setPublicationData] = React.useState(null);
  const [contentData, setContentData] = React.useState(null);
  const [path_to_folder, setPath_to_folder] = React.useState(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (publication) {
      setPublicationData(publication);
    }
  }, [publication]);

  React.useEffect(() => {
    if (publicationData) {
      // Парсим content
      if (publicationData.content) {
        try {
          const parsedContent = JSON.parse(publicationData.content);
          setContentData(parsedContent);
        } catch (error) {
          setContentData(null);
        }
      }

      // Создаем path_to_folder
      if (publicationData.path && publicationData.eng_name) {
        const pathValue = `https://${publicationData.path}${publicationData.eng_name}/`;
        setPath_to_folder(pathValue);
      }
    }
  }, [publicationData]);

  // Извлекаем отзывы из contentData
  const reviews = React.useMemo(() => {
    if (!contentData || !Array.isArray(contentData)) return [];

    // Ищем блоки с отзывами
    const reviewsBlocks = contentData.filter(
      (block) => (block.boock_tag === 'review_block' || block.block_tag === 'review_block') || (block.boock_tag === 'reviews_block' || block.block_tag === 'reviews_block'),
    );

    if (reviewsBlocks.length === 0) {
      // Если нет специальных блоков, пытаемся найти отзывы в общих блоках
      return contentData
        .map((block) => {
          if (!block.block_content || !Array.isArray(block.block_content)) return null;

          const reviewData = {
            name: null,
            text: null,
            rating: null,
            image: null,
            date: null,
          };

          block.block_content.forEach((content) => {
            if (content.cotent_tag === 'name' || content.content_tag === 'name') {
              reviewData.name = content.content_text;
            } else if (content.cotent_tag === 'review' || content.content_tag === 'review') {
              reviewData.text = content.content_text;
            } else if (content.cotent_tag === 'stars' || content.content_tag === 'stars') {
              reviewData.rating = content.content_text;
            } else if (content.cotent_tag === 'avatar' || content.content_tag === 'avatar') {
              if (content.img_name && content.img_name !== 'none') {
                const imgName = content.img_name.split('/').pop().split('\\').pop();
                reviewData.image = imgName;
              }
            } else if (content.cotent_tag === 'review_data' || content.content_tag === 'review_data') {
              reviewData.date = content.content_text;
            }
          });

          // Возвращаем отзыв только если есть хотя бы имя или текст
          return reviewData.name || reviewData.text ? reviewData : null;
        })
        .filter((review) => review !== null);
    }

    // Обрабатываем специальные блоки отзывов
    return reviewsBlocks
      .map((block) => {
        if (!block.block_content || !Array.isArray(block.block_content)) return null;

        const reviewData = {
          name: null,
          text: null,
          rating: null,
          image: null,
          date: null,
        };

        block.block_content.forEach((content) => {
          if (content.cotent_tag === 'name' || content.content_tag === 'name') {
            reviewData.name = content.content_text;
          } else if (content.cotent_tag === 'review' || content.content_tag === 'review') {
            reviewData.text = content.content_text;
          } else if (content.cotent_tag === 'stars' || content.content_tag === 'stars') {
            reviewData.rating = content.content_text;
          } else if (content.cotent_tag === 'avatar' || content.content_tag === 'avatar') {
            if (content.img_name && content.img_name !== 'none') {
              const imgName = content.img_name.split('/').pop().split('\\').pop();
              reviewData.image = imgName;
            }
          } else if (content.cotent_tag === 'review_data' || content.content_tag === 'review_data') {
            reviewData.date = content.content_text;
          }
        });

        return reviewData.name || reviewData.text ? reviewData : null;
      })
      .filter((review) => review !== null);
  }, [contentData]);

  // Проверяем последний блок на наличие link_block
  const moreReviewsLink = React.useMemo(() => {
    if (!contentData || !Array.isArray(contentData) || contentData.length === 0) return null;

    const lastBlock = contentData[contentData.length - 1];
    
    if ((lastBlock?.boock_tag === 'link_block' || lastBlock?.block_tag === 'link_block') && lastBlock?.block_content && Array.isArray(lastBlock.block_content)) {
      const linkContent = lastBlock.block_content.find(
        (content) => content.content_text && content.content_text !== 'none'
      );
      
      if (linkContent && linkContent.content_text) {
        return linkContent.content_text;
      }
    }
    
    return null;
  }, [contentData]);

  // Обработчик колесика мыши (как в компоненте ленты новостей)
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  if (!reviews || reviews.length === 0) {
    return null;
  }

  return (
    <div className={styles.reviewsV1}>
      <div className={styles.reviewsContainer}>
        <h2 className={styles.reviewsTitle}>Отзывы</h2>
        <div className={styles.reviewsScroll} ref={containerRef}>
          {reviews.map((review, index) => (
            <div key={index} className={styles.reviewCard}>
              {review.image && path_to_folder && (
                <div className={styles.reviewImage}>
                  <img src={path_to_folder + review.image} alt={review.name || 'Review'} />
                </div>
              )}
              <div className={styles.reviewContent}>
                {review.name && <h3 className={styles.reviewName}>{review.name}</h3>}
                {review.rating && (
                  <div className={styles.reviewRating}>
                    {'★'.repeat(Math.min(parseInt(review.rating) || 5, 5))}
                  </div>
                )}
                {review.text && <p className={styles.reviewText}>{review.text}</p>}
                {review.date && <span className={styles.reviewDate}>{review.date}</span>}
              </div>
            </div>
          ))}
        </div>
        {moreReviewsLink && (
          <div className={styles.moreReviewsButtonContainer}>
            <a href={moreReviewsLink} className={styles.moreReviewsButton}>
              все отзывы
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsV1;

